<script setup lang="ts">
/**
 * H15 — Home Menu drawer
 * Figma RedisignHomePage `50:10284` desktop (440×100vh) · `50:10382` mobile (full screen)
 * States: 1 Root (gender tabs + top-level + Footer Language/Currency) ·
 * 2 Drilled 1 lvl (Back + title + View all + children) · 3 Drilled 2+ (any depth).
 * Shell mirrors `FairHomeV2SearchDrawer` (Teleport → mask → panel).
 *
 * Rows come from `GET /api/v1/categories/tree` (CATEGORY-TREE, 2026-09-14):
 * the normalised taxonomy counted per tab, replacing the legacy
 * `/shopping-list/category-tree` (the `category` table: Ashtrays and Aviator
 * Watches under Women). Markup and styles are the Figma ones, unchanged.
 */
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import Skeleton from 'primevue/skeleton'
import { useCategoryTreeV1Query } from '@/queries/categories'
import { useLocaleStore } from '@/shared/store/useLocaleStore'
import { FairIcon } from '@/ui/primitives/FairIcon'
import { FairIconButton } from '@/ui/primitives/FairIconButton'
import FairHomeV2MenuLocaleModal from './FairHomeV2MenuLocaleModal.vue'
import {
  buildFairHomeV2MenuLocation,
  FAIR_HOME_V2_MENU_DRAWER_WIDTH_DESKTOP,
  FAIR_HOME_V2_MENU_GENDER_TABS,
  findFairHomeV2MenuCurrency,
  findFairHomeV2MenuLanguage,
  isFairHomeV2MenuLeaf,
  persistFairHomeV2MenuCurrency,
  persistFairHomeV2MenuLanguage,
  readFairHomeV2MenuCurrency,
  readFairHomeV2MenuLanguage,
  toFairHomeV2MenuNodes,
  toFairHomeV2MenuTreeAudience,
  toFairHomeV2MenuTreeLang,
  type FairHomeV2MenuLocaleKind,
  type FairHomeV2MenuNode,
} from './fair-home-v2-menu-drawer.data'
import { useFairHomeV2MenuStack } from './useFairHomeV2MenuStack'
import './fair-home-v2-menu-drawer.css'

const props = withDefaults(
  defineProps<{
    open?: boolean
    compact?: boolean
    /** Header gender tab value (`women` / `men` / `kids`). */
    audience?: string
  }>(),
  {
    open: false,
    compact: false,
    audience: 'women',
  },
)

const emit = defineEmits<{
  'update:open': [value: boolean]
  'update:audience': [value: string]
}>()

const { t } = useI18n()
const router = useRouter()
const localeStore = useLocaleStore()

/* ------------------------------------------------------------------ */
/* Taxonomy                                                            */
/* ------------------------------------------------------------------ */

// CATEGORY-TREE: one tree per (tab, language) from the backend — the tab is
// the request's audience, never a row; RU labels under the RU locale.
const treeAudience = computed(() => toFairHomeV2MenuTreeAudience(props.audience))
const treeLang = computed(() => toFairHomeV2MenuTreeLang(localeStore.currentLocale))
const categoryTreeQuery = useCategoryTreeV1Query(treeAudience, treeLang)
const roots = computed(() => toFairHomeV2MenuNodes(categoryTreeQuery.data.value))
const isLoading = computed(
  () => categoryTreeQuery.isLoading.value && roots.value.length === 0,
)
const isError = computed(() => categoryTreeQuery.isError.value && roots.value.length === 0)

/** Figma ListRow height, six placeholders while the tree loads. */
const SKELETON_ROWS = 6

function retryTree() {
  void categoryTreeQuery.refresh(true)
}

const { stack, isRoot, rows, title, push, pop, reset, pathTo } = useFairHomeV2MenuStack(roots)

/* ------------------------------------------------------------------ */
/* Gender tabs — pinned, change resets stack                           */
/* ------------------------------------------------------------------ */

const genderTabs = FAIR_HOME_V2_MENU_GENDER_TABS

function selectGender(value: string) {
  if (value !== props.audience) {
    emit('update:audience', value)
  }
  reset()
}

/* ------------------------------------------------------------------ */
/* Rows                                                                */
/* ------------------------------------------------------------------ */

function navigateTo(path: FairHomeV2MenuNode[]) {
  const location = buildFairHomeV2MenuLocation(path, props.audience)
  if (!location) return
  close()
  void router.push(location)
}

function handleRow(node: FairHomeV2MenuNode) {
  if (isFairHomeV2MenuLeaf(node)) {
    navigateTo(pathTo(node))
    return
  }
  push(node)
  scrollRowsTop()
}

function handleBack() {
  pop()
  scrollRowsTop()
}

/** View all → every product under the current branch (root … current). */
function handleViewAll() {
  navigateTo([...stack.value])
}

const rowsRef = ref<HTMLElement | null>(null)

function scrollRowsTop() {
  rowsRef.value?.scrollTo({ top: 0 })
}

/* ------------------------------------------------------------------ */
/* Footer — Language / Currency                                        */
/* ------------------------------------------------------------------ */

const languageId = ref(readFairHomeV2MenuLanguage(localeStore.currentLocale))
const currencyId = ref(readFairHomeV2MenuCurrency())

const languageShort = computed(() => findFairHomeV2MenuLanguage(languageId.value).short)
const currencyLabel = computed(() => findFairHomeV2MenuCurrency(currencyId.value).label)

const localeModalOpen = ref(false)
const localeModalKind = ref<FairHomeV2MenuLocaleKind>('language')
const localeModalValue = computed(() =>
  localeModalKind.value === 'language' ? languageId.value : currencyId.value,
)

function openLocaleModal(kind: FairHomeV2MenuLocaleKind) {
  localeModalKind.value = kind
  localeModalOpen.value = true
}

function handleLocaleApply(kind: FairHomeV2MenuLocaleKind, value: string) {
  if (kind === 'language') {
    languageId.value = value
    persistFairHomeV2MenuLanguage(value)
    const option = findFairHomeV2MenuLanguage(value)
    // Only `en` / `ru` are shipped locales; other picks are stored as a
    // preference and fall back to the current UI locale.
    if (option.locale) localeStore.setLocale(option.locale)
    return
  }
  currencyId.value = value
  // Same storage as legacy FairLocaleMenu; no reload here — prices re-read
  // `currency` on next request, and a reload would drop the drawer state.
  persistFairHomeV2MenuCurrency(value)
}

/* ------------------------------------------------------------------ */
/* Shell                                                               */
/* ------------------------------------------------------------------ */

const rootClass = computed(() => ({
  'fair-home-v2-menu': true,
  'fair-home-v2-menu--compact': props.compact,
  'fair-home-v2-menu--desktop': !props.compact,
  'fair-home-v2-menu--root': isRoot.value,
  'fair-home-v2-menu--drilled': !isRoot.value,
}))

const panelStyle = computed(() => ({
  '--fair-h2-menu-drawer-w': `${FAIR_HOME_V2_MENU_DRAWER_WIDTH_DESKTOP}px`,
}))

function close() {
  emit('update:open', false)
}

function handleKeydown(event: KeyboardEvent) {
  if (event.key !== 'Escape' || localeModalOpen.value) return
  event.preventDefault()
  close()
}

watch(
  () => props.open,
  (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
    if (isOpen) {
      document.addEventListener('keydown', handleKeydown)
    } else {
      document.removeEventListener('keydown', handleKeydown)
      localeModalOpen.value = false
      reset()
    }
  },
  { immediate: true },
)

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = ''
})
</script>

<template>
  <Teleport to="body">
    <Transition name="fair-h2-menu">
      <div
        v-if="open"
        :class="rootClass"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="t('fair_home_v2.menu.title')"
        data-unit="H15"
        :data-figma="compact ? '50:10382' : '50:10284'"
      >
        <button
          type="button"
          class="fair-home-v2-menu__mask"
          :aria-label="t('fair_home_v2.menu.close')"
          @click="close"
        />

        <aside class="fair-home-v2-menu__panel">
          <header class="fair-home-v2-menu__header">
            <span
              v-if="compact"
              class="fair-home-v2-menu__header-spacer"
              aria-hidden="true"
            />
            <h2 class="fair-home-v2-menu__title">
              {{ t('fair_home_v2.menu.title') }}
            </h2>
            <FairIconButton
              icon="close"
              :size="compact ? 'mobile' : 'medium'"
              :aria-label="t('fair_home_v2.menu.close')"
              @click="close"
            />
          </header>
          <div
            v-if="!compact"
            class="fair-home-v2-menu__rule"
            aria-hidden="true"
          />

          <div class="fair-home-v2-menu__content">
            <!-- Gender tabs — MenuNav 50:9865 / MenuTab 50:9840, pinned at every depth -->
            <nav
              class="fair-home-v2-menu__tabs"
              :aria-label="t('fair_home_v2.menu.gender_tabs')"
            >
              <button
                v-for="tab in genderTabs"
                :key="tab.value"
                type="button"
                class="fair-home-v2-menu__tab"
                :class="{ 'fair-home-v2-menu__tab--active': tab.value === audience }"
                :aria-pressed="tab.value === audience"
                @click="selectGender(tab.value)"
              >
                {{ tab.label }}
              </button>
            </nav>

            <!-- States 2–3: Back + title + View all -->
            <template v-if="!isRoot">
              <button
                type="button"
                class="fair-home-v2-menu__back"
                @click="handleBack"
              >
                <FairIcon
                  name="arrow-left"
                  :size="compact ? 16 : 12"
                />
                <span>{{ t('fair_home_v2.menu.back') }}</span>
              </button>

              <div class="fair-home-v2-menu__heading">
                <h3 class="fair-home-v2-menu__heading-title">
                  {{ title }}
                </h3>
                <button
                  type="button"
                  class="fair-home-v2-menu__view-all"
                  @click="handleViewAll"
                >
                  {{ t('fair_home_v2.menu.view_all') }}
                </button>
              </div>
            </template>

            <!-- Rows — ListRow Type=Menu 50:9620 / 50:9625 -->
            <ul
              ref="rowsRef"
              class="fair-home-v2-menu__rows"
              :aria-busy="isLoading"
            >
              <li
                v-for="node in rows"
                :key="node.id"
              >
                <button
                  type="button"
                  class="fair-home-v2-menu__row"
                  :class="{ 'fair-home-v2-menu__row--leaf': isFairHomeV2MenuLeaf(node) }"
                  @click="handleRow(node)"
                >
                  <span class="fair-home-v2-menu__row-label">{{ node.label }}</span>
                  <FairIcon
                    v-if="!isFairHomeV2MenuLeaf(node)"
                    class="fair-home-v2-menu__row-chevron"
                    name="arrow-forward"
                    :size="12"
                  />
                </button>
              </li>
              <!-- Loading: skeleton rows at the ListRow height (design-system Skeleton, as the capsules shelf) -->
              <li
                v-for="index in isLoading ? SKELETON_ROWS : 0"
                :key="`skeleton-${index}`"
                class="fair-home-v2-menu__skeleton"
                aria-hidden="true"
              >
                <Skeleton class="fair-home-v2-menu__skeleton-label" />
              </li>
              <!-- Error: the tree did not arrive — say so and offer a retry, never an empty "No categories" -->
              <li
                v-if="isError"
                class="fair-home-v2-menu__empty"
                role="alert"
              >
                <span>{{ t('fair_home_v2.menu.error') }}</span>
                <button
                  type="button"
                  class="fair-home-v2-menu__retry"
                  @click="retryTree"
                >
                  {{ t('fair_home_v2.menu.retry') }}
                </button>
              </li>
              <li
                v-if="!isLoading && !isError && rows.length === 0"
                class="fair-home-v2-menu__empty"
              >
                {{ t('fair_home_v2.menu.empty') }}
              </li>
            </ul>
          </div>

          <!-- State 1 only: Footer Language / Currency -->
          <template v-if="isRoot">
            <div
              v-if="!compact"
              class="fair-home-v2-menu__rule"
              aria-hidden="true"
            />
            <footer class="fair-home-v2-menu__footer">
              <button
                type="button"
                class="fair-home-v2-menu__footer-row"
                @click="openLocaleModal('language')"
              >
                <span class="fair-home-v2-menu__footer-label">
                  {{ t('fair_home_v2.menu.language') }}
                </span>
                <span class="fair-home-v2-menu__footer-value">{{ languageShort }}</span>
              </button>
              <button
                type="button"
                class="fair-home-v2-menu__footer-row"
                @click="openLocaleModal('currency')"
              >
                <span class="fair-home-v2-menu__footer-label">
                  {{ t('fair_home_v2.menu.currency') }}
                </span>
                <span class="fair-home-v2-menu__footer-value">{{ currencyLabel }}</span>
              </button>
            </footer>
          </template>
        </aside>
      </div>
    </Transition>
  </Teleport>

  <FairHomeV2MenuLocaleModal
    v-model:open="localeModalOpen"
    :kind="localeModalKind"
    :compact="compact"
    :value="localeModalValue"
    @apply="handleLocaleApply"
  />
</template>
